import React, { useState } from 'react';
import { Trash2, Home, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { ProposedPlan, ProposedPage } from '../../../api/modules/roaryImport';
import { ColorPickerPopover } from '../../../pages/ThemeManager/ColorPickerPopover';
import { colorDetected } from './importBrandDisplay';

const BRAND_COLOR_KEYS = ['primary', 'secondary', 'accent', 'background', 'text'] as const;

function hexForPicker(value: string | undefined, fallback: string): string {
  const trimmed = (value || '').trim();
  const hex6 = trimmed.match(/^#([0-9A-Fa-f]{6})$/);
  if (hex6) return `#${hex6[1]}`;
  const hex3 = trimmed.match(/^#([0-9A-Fa-f]{3})$/);
  if (hex3) {
    return `#${hex3[1]
      .split('')
      .map(ch => ch + ch)
      .join('')}`;
  }
  return fallback;
}

interface ImportPlanReviewProps {
  plan: ProposedPlan;
  warnings: string[];
  onConfirm: (editedPlan: ProposedPlan) => void;
  onCancel: () => void;
}

export const ImportPlanReview: React.FC<ImportPlanReviewProps> = ({
  plan,
  warnings,
  onConfirm,
  onCancel,
}) => {
  const [pages, setPages] = useState<ProposedPage[]>(plan.pages);
  const [colors, setColors] = useState(plan.brand.colors);
  const [brandName, setBrandName] = useState(plan.brand.brandName ?? plan.brand.siteName ?? '');

  const updatePage = (idx: number, patch: Partial<ProposedPage>) => {
    setPages(p => p.map((page, i) => (i === idx ? { ...page, ...patch } : page)));
  };

  const dropPage = (idx: number) => {
    setPages(p => p.filter((_, i) => i !== idx));
  };

  const setHomePage = (idx: number) => {
    setPages(p => p.map((page, i) => ({ ...page, isHomePage: i === idx })));
  };

  const handleConfirm = () => {
    if (pages.length === 0) return;
    if (!pages.some(p => p.isHomePage)) {
      // Force the first page to be the home page if user dropped the original.
      pages[0].isHomePage = true;
    }
    onConfirm({
      ...plan,
      brand: {
        ...plan.brand,
        brandName: brandName.trim() || plan.brand.brandName,
        siteName: brandName.trim() || plan.brand.siteName,
        colors,
      },
      pages,
    });
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      <div className="px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2 text-green-400 mb-1">
          <CheckCircle2 className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">Plan ready</span>
        </div>
        <h2 className="text-lg font-semibold">Review the proposed site</h2>
        <p className="text-sm text-slate-400 mt-1">
          Roary suggests {pages.length} pages. Edit titles or slugs, drop pages, or pick a different
          home page before building.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {/* Brand colors editor */}
        <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Brand name</div>
            <input
              type="text"
              value={brandName}
              onChange={e => setBrandName(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded bg-slate-900 border border-slate-700 focus:border-orange-500 outline-none text-white"
              placeholder="Business name shown in the navbar"
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Brand colors</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {BRAND_COLOR_KEYS.map(key => {
                const detected = colorDetected(colors[key]);
                const fallback =
                  key === 'background' ? '#ffffff' : detected ? '#000000' : '#64748b';
                return (
                  <div key={key}>
                    <ColorPickerPopover
                      label={key}
                      variant="dark"
                      color={hexForPicker(colors[key], fallback)}
                      onChange={next => setColors(c => ({ ...c, [key]: next }))}
                    />
                    {!detected && (
                      <p className="mt-1 text-[10px] text-slate-500">Not detected — pick a color</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pages */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-400">Pages</div>
          {pages.map((page, idx) => (
            <div
              key={idx}
              className="rounded-lg bg-slate-800/40 border border-slate-700 p-3 flex items-center gap-3"
            >
              <button
                onClick={() => setHomePage(idx)}
                className={`p-1.5 rounded transition-colors ${
                  page.isHomePage
                    ? 'bg-orange-500/20 text-orange-300'
                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700'
                }`}
                title={page.isHomePage ? 'This is the home page' : 'Make this the home page'}
              >
                <Home size={14} />
              </button>
              <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={page.title}
                  onChange={e => updatePage(idx, { title: e.target.value })}
                  className="px-2 py-1.5 text-sm rounded bg-slate-900 border border-slate-700 focus:border-orange-500 outline-none text-white"
                  placeholder="Page title"
                />
                <input
                  type="text"
                  value={page.slug}
                  onChange={e => updatePage(idx, { slug: e.target.value.toLowerCase() })}
                  className="px-2 py-1.5 text-sm font-mono rounded bg-slate-900 border border-slate-700 focus:border-orange-500 outline-none text-white"
                  placeholder={page.isHomePage ? '(home)' : 'slug'}
                />
              </div>
              <span className="text-xs text-slate-500">
                {page.components.length} component{page.components.length === 1 ? '' : 's'}
              </span>
              <button
                onClick={() => dropPage(idx)}
                disabled={pages.length === 1}
                className="p-1.5 rounded text-red-400 hover:bg-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Remove this page"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        {warnings.length > 0 && (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4">
            <div className="flex items-center gap-2 text-amber-300 mb-2">
              <AlertTriangle size={14} />
              <span className="text-xs font-semibold uppercase tracking-wider">Warnings</span>
            </div>
            <ul className="text-xs text-amber-200 space-y-1 list-disc list-inside">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-800 flex items-center justify-between gap-3">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white">
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={pages.length === 0}
          className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold transition-colors"
        >
          Build the site
        </button>
      </div>
    </div>
  );
};
